import type {$ZodIssueTooSmall, $ZodIssueTooBig} from 'zod/v4/core';
import {i18nInstance} from '@myparcel-frontend/i18n';
import {formatters} from '@myparcel-frontend/utils';
import {z} from 'zod';

export const loadZodErrorTranslations = () => {
  const t: ((key: string, options?: Record<string, unknown>) => string) | undefined = i18nInstance.value?.global.t;

  if (!t) {
    console.warn('i18n instance is not available, skipping Zod error translations');
    return;
  }

  const customError = (iss: z.core.$ZodIssue) => {
    let customMessage: string;

    switch (iss.origin) {
      case 'phone':
        customMessage = t('errors.specificField.phone.invalid');
        break;
      case 'currency':
        const formattedMaxAmount = formatters.currency.format(iss.maximum, {
          locale: i18nInstance.value?.global.locale,
        });

        customMessage = t('errors.field.maxNumber', {
          maxNumber: formattedMaxAmount,
        });
        break;
      default:
        customMessage = t('errors.field.invalid');
    }

    return customMessage;
  };

  const getCurrentLengthFromValue = (currentValue: unknown) => {
    if (typeof currentValue === 'string') {
      return currentValue.length;
    }
  };

  const getConstraintTranslations = ({
    code,
    origin,
    constraint,
    currentValue,
    inclusive,
  }: {
    code: $ZodIssueTooBig['code'] | $ZodIssueTooSmall['code'];
    origin: $ZodIssueTooBig['origin'];
    constraint: $ZodIssueTooBig['maximum'];
    currentValue: unknown;
    inclusive?: boolean;
  }): string => {
    const longerErrorMessageLength = 20;
    const path = `${code}.${origin}`;
    const useLongMessage = constraint > longerErrorMessageLength;

    const requiredMessage = constraint === 1;

    const currentLength = getCurrentLengthFromValue(currentValue);

    const translations: Record<string, string> = {
      'too_small.string': requiredMessage
        ? t('errors.field.required')
        : t('errors.length.min', {
            minLength: constraint,
          }),
      // Exclusive minimum (e.g. z.number().gt(0)): "minimaal 0" would wrongly allow 0 itself.
      'too_small.number':
        inclusive === false
          ? t('errors.field.greaterThanNumber', {minNumber: constraint})
          : t('errors.field.minNumber', {
              minNumber: constraint,
            }),
      'too_big.string': useLongMessage
        ? t('errors.length.maxAndCurrent', {
            maxLength: constraint,
            currentLength,
          })
        : t('errors.length.max', {maxLength: constraint}),
      'too_big.number': t('errors.field.maxNumber', {maxNumber: constraint}),
    };

    return translations[path];
  };

  z.config({
    customError: (iss) => {
      let message: string;

      switch (iss.code) {
        // Because `<input type="number">` elements are initialized with empty strings (default behavior of HTML).
        // When the user submits the form Zod thinks the values are an `invalid_type` instead of required.
        // So we translate `invalid_type` on an EMPTY input to required. A filled-in value that has
        // the wrong type (e.g. "21%" parsed to NaN, or 2.5 where an integer is expected) gets a
        // message describing what is actually wrong instead.
        case 'invalid_type': {
          const {input} = iss;

          if (input === undefined || input === null || input === '') {
            message = t('errors.field.required');
          } else if (typeof input === 'number' && Number.isNaN(input)) {
            message = t('errors.field.notANumber');
          } else if (iss.expected === 'int' && typeof input === 'number') {
            message = t('errors.field.wholeNumber');
          } else {
            message = t('errors.field.invalid');
          }
          break;
        }
        case 'invalid_format':
          message = iss.format === 'datetime' ? t('errors.field.invalidDate') : t('errors.field.invalid');
          break;
        case 'too_big':
          message = getConstraintTranslations({
            code: iss.code,
            origin: iss.origin,
            constraint: iss.maximum,
            currentValue: iss.input,
          });
          break;
        case 'too_small':
          message = getConstraintTranslations({
            code: iss.code,
            origin: iss.origin,
            constraint: iss.minimum,
            currentValue: iss.input,
            inclusive: iss.inclusive,
          });
          break;
        case 'custom':
          message = customError(iss);
          break;
        // A union reports one issue for the whole object (member sub-errors are nested), so it is
        // never about a single empty field — "required" would be a lie on a filled-in value.
        case 'invalid_union':
          message = t('errors.field.invalid');
          break;
        default:
          message = t('errors.field.required');
          break;
      }

      return message;
    },
  });
};
